import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Gift, 
  Percent, 
  Tag, 
  CheckCircle, 
  Clock, 
  XCircle,
  Copy,
  Package
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import type { User, Redemption as DbRedemption, Restaurant } from "@shared/schema";

// Frontend type where timestamps are strings (from JSON serialization)
type Redemption = Omit<DbRedemption, 'redeemedAt' | 'usedAt' | 'expiresAt'> & {
  redeemedAt: string | null;
  usedAt: string | null;
  expiresAt: string | null;
};

export default function RedemptionHistory() {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const { toast } = useToast();

  const { data: user, isLoading: userLoading } = useQuery<User>({
    queryKey: ['/api/user']
  });

  const { data: restaurants } = useQuery<Restaurant[]>({
    queryKey: ['/api/restaurants']
  });

  const { data: redemptions, isLoading: redemptionsLoading } = useQuery<Redemption[]>({
    queryKey: ['/api/redemptions/history'],
    enabled: !!user
  });

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'discount':
        return <Percent className="w-5 h-5" />;
      case 'free_item':
        return <Gift className="w-5 h-5" />;
      case 'gift_certificate':
        return <Tag className="w-5 h-5" />;
      default:
        return <Gift className="w-5 h-5" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'redeemed':
        return <CheckCircle className="w-4 h-4" />;
      case 'expired':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
      case 'redeemed':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'expired':
        return 'bg-red-500/10 text-red-600 dark:text-red-400';
      default:
        return '';
    }
  };

  const getRewardTypeColor = (type: string) => {
    switch (type) {
      case 'discount':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
      case 'free_item':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'gift_certificate':
        return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
      default:
        return '';
    }
  };

  const getRestaurantName = (restaurantId: string) => {
    const restaurant = restaurants?.find(r => r.id === restaurantId);
    return restaurant?.name || 'Unknown Restaurant';
  };

  const copyVoucherCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied!",
      description: `Voucher code ${code} copied to clipboard.`
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const filteredRedemptions = redemptions?.filter(r => {
    if (selectedStatus === 'all') return true;
    if (selectedStatus === 'expired') return isExpired(r.expiresAt || null);
    return r.status === selectedStatus;
  }) || [];

  const statusCounts = {
    all: redemptions?.length || 0,
    pending: redemptions?.filter(r => r.status === 'pending' && !isExpired(r.expiresAt || null)).length || 0,
    redeemed: redemptions?.filter(r => r.status === 'redeemed').length || 0,
    expired: redemptions?.filter(r => isExpired(r.expiresAt || null)).length || 0
  };

  if (userLoading || redemptionsLoading) {
    return (
      <div className="overflow-y-auto pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-muted rounded-lg"></div>
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-32 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto pb-20 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">My Redemptions</h1>
          <p className="text-muted-foreground mt-1">
            View your reward redemptions and voucher codes
          </p>
        </div>

        {/* Status Tabs */}
        <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all" data-testid="tab-all">
              All ({statusCounts.all})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Active ({statusCounts.pending})
            </TabsTrigger>
            <TabsTrigger value="redeemed" data-testid="tab-redeemed">
              Used ({statusCounts.redeemed})
            </TabsTrigger>
            <TabsTrigger value="expired" data-testid="tab-expired">
              Expired ({statusCounts.expired})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={selectedStatus} className="mt-6 space-y-4">
            {filteredRedemptions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {selectedStatus === 'all' 
                      ? 'No redemptions yet. Visit the Redeem page to start earning rewards!'
                      : `No ${selectedStatus} redemptions.`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredRedemptions.map((redemption) => {
                const metadata = redemption.metadata as any;
                const expired = isExpired(redemption.expiresAt || null);
                const actualStatus = expired ? 'expired' : redemption.status;

                return (
                  <Card 
                    key={redemption.id}
                    className="hover-elevate"
                    data-testid={`redemption-${redemption.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={cn(
                          "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
                          getRewardTypeColor(metadata?.itemType || '')
                        )}>
                          {getRewardIcon(metadata?.itemType || '')}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Title and Status */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">
                                {metadata?.itemTitle || 'Reward'}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {getRestaurantName(redemption.restaurantId)}
                              </p>
                            </div>
                            <Badge className={cn("text-xs", getStatusColor(actualStatus))}>
                              <div className="flex items-center gap-1">
                                {getStatusIcon(actualStatus)}
                                <span className="capitalize">{actualStatus}</span>
                              </div>
                            </Badge>
                          </div>

                          {/* Description */}
                          {metadata?.itemDescription && (
                            <p className="text-sm text-muted-foreground mb-3">
                              {metadata.itemDescription}
                            </p>
                          )}

                          {/* Voucher Code */}
                          <div className="bg-muted p-4 rounded-lg mb-3">
                            <p className="text-xs text-muted-foreground mb-1">Voucher Code</p>
                            <div className="flex items-center justify-between gap-2">
                              <p 
                                className="text-2xl font-bold tracking-wider font-mono"
                                data-testid={`voucher-code-${redemption.id}`}
                              >
                                {redemption.voucherCode}
                              </p>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => copyVoucherCode(redemption.voucherCode)}
                                data-testid={`button-copy-${redemption.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Metadata */}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>
                                Redeemed {redemption.redeemedAt ? new Date(redemption.redeemedAt).toLocaleDateString() : 'Recently'}
                              </span>
                            </div>
                            {redemption.expiresAt && (
                              <>
                                <span>•</span>
                                <span className={cn(expired && "text-destructive")}>
                                  {expired ? 'Expired' : 'Expires'} {new Date(redemption.expiresAt).toLocaleDateString()}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Terms */}
                          {metadata?.termsConditions && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-muted-foreground">
                                <strong>Terms:</strong> {metadata.termsConditions}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
